import { useEffect, useState } from 'react';
import { getSpaces, addChatToSpace } from '../../api/api';
import { toast } from 'react-hot-toast';
import { X, Loader2, FolderOpen, Plus } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { fetchChatSessions } from '../../redux/slices/chatSessionsSlice';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { handleApiError } from '../../helpers/handleApiError';

export default function AddToSpaceModal({
  isOpen,
  onClose,
  chatId,
  uid,
  refreshChatHistory,
  isDarkMode,
}) {
  const [spaces, setSpaces] = useState([]);
  const [loading, setLoading] = useState(false);
  const [adding, setAdding] = useState(false);
  const [selectedSpace, setSelectedSpace] = useState(null);
  const dispatch = useDispatch();
  const navigate = useNavigate();

  useEffect(() => {
    if (isOpen) {
      (async () => {
        setLoading(true);
        try {
          const data = await getSpaces(uid);
          setSpaces(data.spaces || []);
        } catch (err) {
          handleApiError(err, 'Failed to fetch spaces');
        } finally {
          setLoading(false);
        }
      })();
    } else {
      setSelectedSpace(null);
    }
  }, [isOpen]);

  const handleAdd = async () => {
    if (!selectedSpace) {
      toast.error('Please select a space first');
      return;
    }

    setAdding(true);

    try {
      const res = await addChatToSpace(selectedSpace.space_id, chatId, uid);

      if (res?.status === 'error') {
        if (res?.error?.toLowerCase().includes('already')) {
          toast(res.error, { icon: '⚠️', duration: 4000 });
        } else {
          toast.error(res?.error || 'Failed to add chat to space ❌');
        }
      } else {
        toast.success(
          <span title={res?.message || selectedSpace.name}>
            {res?.message
              ? formatName(res.message, 40)
              : `Chat added to "${formatName(
                  selectedSpace.name
                )}" successfully ✅`}
          </span>
        );

        if (refreshChatHistory) {
          refreshChatHistory();
        }
        dispatch(fetchChatSessions());

        onClose();
      }
    } catch (err) {
      const message =
        err?.response?.data?.error ||
        err?.response?.data?.message ||
        err?.message ||
        'Something went wrong ❌';

      if (message.toLowerCase().includes('already exists')) {
        toast(message, { icon: '⚠️', duration: 4000 });
      } else {
        toast.error(message, { duration: 4000 });
      }

      console.error('handleAdd error:', message);
    } finally {
      setAdding(false);
    }
  };

  const formatName = (name, maxLength = 32) => {
    if (!name || name.length <= maxLength) return name;

    const keep = Math.floor((maxLength - 3) / 2);
    return `${name.slice(0, keep)}...${name.slice(-keep)}`;
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          className="fixed inset-0 z-50 flex items-center justify-center"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
          {/* Overlay */}
          <motion.div
            className="absolute inset-0 bg-black bg-opacity-50 backdrop-blur-sm"
            onClick={onClose}
            initial={{ opacity: 0 }}
            animate={{ opacity: 0.5 }}
            exit={{ opacity: 0 }}
          />

          {/* Modal Container */}
          <motion.div
            className={`relative w-full max-w-md rounded-xl shadow-2xl border overflow-hidden ${
              isDarkMode
                ? 'bg-gray-900 border-gray-700 text-white'
                : 'bg-white border-gray-200 text-gray-900'
            } p-6 z-10`}
            initial={{ y: 50, opacity: 0, scale: 0.95 }}
            animate={{ y: 0, opacity: 1, scale: 1 }}
            exit={{ y: 30, opacity: 0, scale: 0.97 }}
            transition={{ type: 'spring', stiffness: 150, damping: 20 }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="relative flex justify-center items-center mb-4 border-b pb-2">
              <h2 className="text-lg font-semibold tracking-wide text-center text-brand">
                Add To Space
              </h2>

              <button
                onClick={onClose}
                className={`absolute right-0 p-1 rounded-full ${
                  isDarkMode
                    ? 'hover:bg-gray-800 text-gray-400'
                    : 'hover:bg-gray-100 text-gray-500'
                }`}
              >
                <X size={20} />
              </button>
            </div>

            {loading ? (
              <div className="flex flex-col items-center justify-center py-10 text-center">
                <Loader2 className="w-6 h-6 animate-spin text-brand mb-2" />
                <p className="text-sm text-gray-400">Loading spaces...</p>
              </div>
            ) : spaces.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-10 text-center">
                <FolderOpen
                  className={`w-10 h-10 mb-3 ${
                    isDarkMode ? 'text-gray-500' : 'text-gray-400'
                  }`}
                />
                <p
                  className={`${
                    isDarkMode ? 'text-gray-400' : 'text-gray-500'
                  } text-sm`}
                >
                  No spaces found.
                </p>
                <p
                  className={`${
                    isDarkMode ? 'text-gray-600' : 'text-gray-400'
                  } text-xs mt-1 mb-4`}
                >
                  Create a space to organize your chats.
                </p>

                <button
                  onClick={() => {
                    onClose();
                    navigate('/app/spaces');
                  }}
                  className={`inline-flex items-center gap-2 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                    isDarkMode
                      ? 'bg-brand text-white hover:bg-indigo-600'
                      : 'bg-brand text-white hover:bg-indigo-700'
                  }`}
                >
                  <Plus className="w-4 h-4" /> Create Space
                </button>
              </div>
            ) : (
              <>
                <motion.div
                  className="grid gap-2 mb-6"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                >
                  {spaces.map((space) => (
                    <motion.div
                      key={space.space_id}
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      onClick={() => setSelectedSpace(space)}
                      className={`cursor-pointer px-4 py-3 rounded-lg border transition-all duration-200 ${
                        selectedSpace?.space_id === space?.space_id
                          ? isDarkMode
                            ? 'border-brand bg-indigo-900/20'
                            : 'border-brand bg-indigo-50'
                          : isDarkMode
                          ? 'border-gray-700 hover:border-gray-600 hover:bg-gray-800/60'
                          : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                      }`}
                    >
                      <div className="font-medium text-sm">{space?.name}</div>
                    </motion.div>
                  ))}
                </motion.div>

                <motion.button
                  whileHover={{ scale: selectedSpace ? 1.02 : 1 }}
                  whileTap={{ scale: selectedSpace ? 0.98 : 1 }}
                  onClick={handleAdd}
                  disabled={!selectedSpace || adding}
                  className={`w-full py-2.5 rounded-md font-semibold text-sm transition-all duration-200 flex items-center justify-center gap-2 ${
                    selectedSpace
                      ? isDarkMode
                        ? 'bg-brand hover:bg-brand text-white shadow-lg shadow-indigo-600/20'
                        : 'bg-brand hover:bg-brand text-white shadow-md'
                      : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                  }`}
                >
                  {adding ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" /> Adding...
                    </>
                  ) : (
                    'Add to Selected Space'
                  )}
                </motion.button>
              </>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
